import React, { FC, useEffect, useState } from "react";
import { Form, Modal, Button, Alert, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import ClipLoader from "react-spinners/ClipLoader";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG_COMMON_CANCEL, LANG_COMMON_DIAGNOSIS, LANG_COMMON_EVOLUTION, LANG_COMMON_LEGAL_ID, LANG_COMMON_LOADING, LANG_COMMON_MEDICAL_RECORD_NUMBER, LANG_COMMON_NAME, LANG_COMMON_NEW, LANG_COMMON_PATIENT, LANG_COMMON_SAVE, LANG_ERROR, LANG_WARNING_FILL_ALL } from "../../../lang"
import InputMask from 'react-input-mask';
import clsx from "clsx";
import { BtnBold, BtnBulletList, BtnItalic, BtnNumberedList, BtnStrikeThrough, BtnUnderline, BtnUndo, Editor, EditorProvider, Separator, Toolbar } from "react-simple-wysiwyg";
import moment from "moment";
import { DocumentModelType } from "../../../types/DocumentType";
import { DocumentModel } from "../../../models/DocumentModel";
import CustomSelect from "../../../components/form/CustomSelect";
import { isResponseError, OptionType, ResponseErrorType } from "../../../types/CommonType";
import Error from "../../../components/messages/Error";
import { toast } from "react-toastify";
import Confirm from "../../../components/messages/Confirm";
interface Props {

}

const DocumentTab: FC<Props> = ({ }) => {
    const schema = yup.object().shape({
        type: yup.string().required(),
        name: yup.string().required(),
        content: yup.string().required(),
        model: yup.string()
    });
    const [formWasSubmitted, setFormWasSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const intl = useIntl();
    const DefaultErrorMsg = intl.formatMessage(LANG_ERROR);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [type, setType] = useState('');
    const [models, setModels] = useState<DocumentModelType[]>([]);
    const [model, setModel] = useState<DocumentModelType | undefined>(undefined);
    const [modelToDelete, setModelToDelete] = useState<DocumentModelType | undefined>(undefined);
    const [noCacheModel, setNoCacheModel] = useState(0);

    const loadModels = (type: string) => {
        setLoadingModels(true);
        DocumentModel()
            .getModels(type)
            .then(
                (models) => {
                    setLoadingModels(false);
                    if (typeof models !== 'boolean') {
                        setModels([...models]);
                        setNoCacheModel(noCacheModel + 1);
                    }
                },
                (err) => {
                    setLoadingModels(false);
                });
    }

    useEffect(() => {
        if (type !== '') {
            loadModels(type);
        } else {
            setModels([]);
            setModel(undefined);
        }
    }, [type]);

    const onSubmit = async () => {
        if (model && model.name.trim() === '') {
            setErrorMsg('Informe um nome para o modelo');
            return;
        }

        if (model && model.model.trim() === '') {
            setErrorMsg('O conteúdo do modelo não pode ser vazio');
            return;
        }

        setLoading(true);
        if (model) {
            const resp: DocumentModelType | ResponseErrorType = model.id === 0 ? await DocumentModel().createModel(model) : await DocumentModel().updateModel(model.id, model);
            setLoading(false);
            if (isResponseError(resp)) {
                setErrorMsg((resp as ResponseErrorType).message || DefaultErrorMsg);
                return;
            } else {
                toast.success(`Modelo ${model.id === 0 ? 'criado' : 'atualizado'} com sucesso!`);
                setModel(resp as DocumentModelType);
                loadModels(type);
            }
        }
    }

    return (
        <>
            <Error
                message={errorMsg}
                show={errorMsg !== ""}
                onHide={() => setErrorMsg("")} />
            {modelToDelete && (
                <Confirm
                    message={`Tem certeza que deseja excluir o modelo "${modelToDelete.name}"? Esta ação não pode ser desfeita.`}
                    show={modelToDelete !== undefined}
                    onHide={() => setModelToDelete(undefined)}
                    onConfirm={async () => {
                        if (modelToDelete) {
                            setLoading(true);
                            setModelToDelete(undefined);
                            const resp = await DocumentModel().removeModel(modelToDelete.id);
                            setLoading(false);
                            if (isResponseError(resp)) {
                                setErrorMsg((resp as ResponseErrorType).message || DefaultErrorMsg);
                                return;
                            } else {
                                toast.success(`Modelo excluído com sucesso!`);
                                setModel(undefined);
                                loadModels(type);
                            }
                        }
                    }} />
            )}
            <Form noValidate onSubmit={(e) => {
                e.preventDefault();
                setFormWasSubmitted(true);
                onSubmit();
            }}>
                <Row>
                    <Col lg={12}>
                        <Alert variant="info">
                            <i className="bi bi-info-circle-fill"></i> Selecione um tipo de documento e um modelo para começar a editar. Você pode criar novos modelos clicando no botão <b><i className="bi bi-plus-lg"></i> Novo</b>.
                        </Alert>
                    </Col>
                    <Col lg={4}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Tipo</Form.Label>
                            <CustomSelect
                                options={DocumentModel().getDocumentTypes()}
                                name="type"
                                onChange={(o: OptionType) => {
                                    setType(o.value);
                                }}
                                placeholder="Selecione..."
                                value={type}
                                isClearable={false}
                            />
                        </Form.Group>
                    </Col>
                    {type !== '' && (
                        <Col lg={4}>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>Modelo</Form.Label>
                                <CustomSelect
                                    key={`models-${type}-${noCacheModel}`}
                                    options={models.map((model) => {
                                        return { 'label': model.name, 'value': model.id }
                                    })}
                                    name="model"
                                    onChange={(o: OptionType) => {
                                        for (let i in models) {
                                            if (models[i].id === o.value) {
                                                setModel(models[i]);
                                            }
                                        }
                                    }}
                                    value={model?.id}
                                    placeholder={loadingModels ? 'Carregando...' : "Selecione..."}
                                    isClearable={false}
                                // disabled={type === '' || loadingModels || document !== undefined}
                                />
                            </Form.Group>
                        </Col>
                    )}
                    <Col lg={4}>
                        <Button
                            disabled={loading}
                            variant="outline-light"
                            onClick={() => {
                                if (type === '') {
                                    setErrorMsg('Selecione um tipo de documento para criar um novo modelo');
                                    return;
                                }
                                setModel({ id: 0, business_id: 0, type: type, model: '', name: '' });
                            }}
                            className="btn-custom btn-custom-light me-2 mt-4">
                            <i className="bi bi-plus-lg"></i> <FormattedMessage {...LANG_COMMON_NEW} />
                        </Button>
                    </Col>
                </Row>
                <Row>
                    {(model) && (
                        <>
                            <Col lg={12}>
                                <div className="title-divider">
                                    <h6>DOCUMENTO</h6>
                                    <hr />
                                </div>
                            </Col>
                            <Col lg={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nome</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        placeholder="Nome ou descrição"
                                        value={model.name}
                                        onChange={(e) => {
                                            setModel({ ...model, name: e.target.value });
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={12}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Documento</Form.Label>
                                    <EditorProvider>
                                        <Editor
                                            name="content"
                                            placeholder={'Documento'}
                                            value={model.model}
                                            onChange={(e) => {
                                                if (model) {
                                                    setModel({ ...model, model: e.target.value });
                                                }
                                            }}
                                        >
                                            <Toolbar>
                                                <BtnBold />
                                                <BtnItalic />
                                                <BtnUnderline />
                                                <BtnStrikeThrough />
                                                <Separator />
                                                <BtnNumberedList />
                                                <BtnBulletList />
                                            </Toolbar>
                                        </Editor>
                                    </EditorProvider>
                                </Form.Group>
                            </Col>
                            <Col lg={12} className="text-end">
                                {model && model.id !== 0 && (
                                    <Button
                                        disabled={loading}
                                        variant="outline-light"
                                        onClick={() => {
                                            setModelToDelete(model);
                                        }}
                                        className="btn-custom btn-custom-light me-2">
                                        <span className="text-danger">
                                            <i className="bi bi-trash"></i> Excluir
                                        </span>
                                    </Button>
                                )}
                                <Button
                                    disabled={loading}
                                    variant="outline-light"
                                    onClick={() => {
                                        setModel(undefined);
                                    }}
                                    className="btn-custom btn-custom-light me-2">
                                    <FormattedMessage {...LANG_COMMON_CANCEL} />
                                </Button>
                                <Button
                                    variant="primary"
                                    type={"submit"}
                                    disabled={loading}
                                    className="btn-custom bg-custom-gradient">
                                    {loading ? <><ClipLoader color={'#ffffff'} loading={loading} size={'12px'} /> <FormattedMessage {...LANG_COMMON_LOADING} /></> : <FormattedMessage {...LANG_COMMON_SAVE} />}
                                </Button>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        </>
    )
}

export default DocumentTab;